import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const EFFECTIVE = "February 26, 2026";

function Section({ n, title, children }) {
  return (
    <section className="border-t border-white/10 py-10 first:border-t-0" data-testid={`privacy-section-${n}`}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFD300]">§ {n.toString().padStart(2, "0")}</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight leading-tight mt-2">{title}</h2>
        </div>
        <div className="col-span-12 md:col-span-9 space-y-4 text-neutral-300 leading-relaxed text-[15px]">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy — Secrecy Executive Training Academy"
        description="How Secrecy Executive Protection LLC collects, uses, stores, and protects your personal information for course registration, Meta lead forms, phone, email, and follow-up communications. Governed by U.S. federal law and the Maryland Personal Information Protection Act."
        path="/privacy"
      />

      {/* Hero */}
      <section className="border-b border-white/10 pt-24 pb-14">
        <div className="container-x">
          <div className="overline mb-4">// LEGAL 001 · EFFECTIVE {EFFECTIVE.toUpperCase()}</div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-4xl">
            Privacy <span className="text-[#FFD300]">Policy.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-2xl text-[15px] leading-relaxed">
            Secrecy Executive Protection LLC ("<strong className="text-white">SEP</strong>," "we," "us," or "our") operates
            seprotection.com and the Secrecy Executive Training Academy (the "<strong className="text-white">Services</strong>").
            This Policy is a binding legal disclosure of how we handle your data. Read it in full before
            submitting any information to us.
          </p>
          <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
            Governed by U.S. federal law and the laws of the State of Maryland.
          </div>
        </div>
      </section>

      <div className="container-x py-6">
        <Section n={1} title="Who we are & how to reach us">
          <p>
            SEP is a Maryland limited liability company operating a state-certified firearms and security training academy.
            You may contact our privacy office at:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Secrecy Executive Protection LLC</li>
            <li>4061 Powder Mill Rd, Suite 500-8, Calverton, MD 20705</li>
            <li>Phone: <a href="tel:+14106450722" className="link-gold">410-645-0722</a></li>
            <li>Email: <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a></li>
          </ul>
        </Section>

        <Section n={2} title="Information we collect">
          <p>We collect only what we need to deliver training, process payments, comply with law, and communicate with you:</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">A. Information you give us directly</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Course registration:</strong> full legal name, date of birth (age verification), residential address, state of residence, phone number, email, emergency contact, prior firearms training, and any disclosures required by state licensing statutes.</li>
            <li><strong className="text-white">Account credentials:</strong> email + hashed password (bcrypt). We never store your password in readable form.</li>
            <li><strong className="text-white">Contact-form &amp; consult submissions:</strong> name, email, phone, and the content of your inquiry.</li>
            <li><strong className="text-white">Payment information:</strong> processed exclusively by our PCI-DSS compliant payment processor (Square, Inc. or Stripe, Inc.). SEP never sees, stores, or logs full card numbers, CVVs, or bank credentials.</li>
          </ul>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">B. Information from Meta lead forms (Facebook &amp; Instagram Lead Ads)</p>
          <p>
            When you submit a Meta lead form (Facebook or Instagram), Meta Platforms, Inc. transmits to us the fields you
            authorized in that form — typically your name, phone number, email, city/state, and any campaign-specific
            responses. That data is imported into our secure customer-relationship system for training follow-up only.
            Meta's own handling of the data on their side is governed by the <a href="https://www.facebook.com/policy.php"
            target="_blank" rel="noopener noreferrer" className="link-gold">Meta Privacy Policy</a>.
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">C. Information collected automatically</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>IP address, browser type, device identifiers, referring page.</li>
            <li>Session cookies (authentication + shopping cart). We do not use third-party ad-tracking cookies on this site at this time.</li>
            <li>Server logs retained for a maximum of 90 days for security and fraud investigation.</li>
          </ul>
        </Section>

        <Section n={3} title="How we use your information">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Deliver training:</strong> enroll you, schedule your class, issue certificates, and satisfy state-licensing recordkeeping obligations (Maryland, D.C., Utah, and other jurisdictions where we hold instructor credentials).</li>
            <li><strong className="text-white">Payments:</strong> pass your purchase to our payment processor, reconcile transactions, and issue refunds under our Refund Policy.</li>
            <li><strong className="text-white">Phone communications:</strong> confirm class dates, deliver last-minute range changes, and respond to inquiries. Calls from our number (410-645-0722) may be recorded for training and quality purposes; you will be notified at the start of any recorded call.</li>
            <li><strong className="text-white">Email communications:</strong> transactional receipts, class reminders, certificate delivery, and (with your consent) course announcements.</li>
            <li><strong className="text-white">Follow-up sequences:</strong> we contact leads and past students by email, phone, and — where you have provided express written consent under the TCPA — SMS. You may opt out at any time (see §7).</li>
            <li><strong className="text-white">Legal &amp; regulatory:</strong> respond to lawful requests from Maryland State Police, D.C. MPD, ATF, and other licensing or law-enforcement authorities.</li>
          </ul>
        </Section>

        <Section n={4} title="Meta lead forms — express notice">
          <p>
            By submitting a Meta lead form (on Facebook or Instagram) that offers training information, quote, or callback from
            SEP, you expressly authorize Secrecy Executive Protection LLC to contact you by <strong className="text-white">phone,
            SMS, and email</strong> at the number and address you provided, for the purpose of scheduling training, sending
            course details, and following up on your inquiry. Message and data rates may apply. Reply <code className="text-[#FFD300]">STOP</code>
            to any SMS to unsubscribe or email <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> with
            "UNSUBSCRIBE" in the subject line.
          </p>
          <p>
            Meta lead data is imported into our system only. We do not resell, license, or transfer lead-form data to any
            third-party marketer.
          </p>
        </Section>

        <Section n={5} title="Follow-up communications & consent">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Email follow-ups:</strong> triggered by course registration, cart activity, or a lead-form submission. Every marketing email includes a one-click unsubscribe link, in compliance with the <em>CAN-SPAM Act (15 U.S.C. § 7701 et seq.)</em>.</li>
            <li><strong className="text-white">SMS follow-ups:</strong> sent only where you have provided prior express written consent, as required by the <em>Telephone Consumer Protection Act (TCPA, 47 U.S.C. § 227)</em> and the <em>Maryland Telephone Consumer Protection Act (Md. Code, Com. Law § 14-3201 et seq.)</em>. Consent is not a condition of purchase.</li>
            <li><strong className="text-white">Phone calls:</strong> we do not use auto-dialed or pre-recorded calls for marketing. Follow-up calls are placed by live staff during business hours (Mon–Sat, 9 AM – 7 PM ET).</li>
            <li><strong className="text-white">Do-not-contact:</strong> once you opt out, we scrub your contact from all marketing lists within 10 business days and retain a minimal suppression record to honor your opt-out going forward.</li>
          </ul>
        </Section>

        <Section n={6} title="How we share your information">
          <p>We do <strong className="text-white">not</strong> sell your personal information. We share it only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Square, Inc. / Stripe, Inc.</strong> — payment processing.</li>
            <li><strong className="text-white">Google Workspace / Gmail SMTP</strong> — transactional email delivery for receipts and confirmations.</li>
            <li><strong className="text-white">Meta Platforms, Inc.</strong> — solely as the source of lead forms you voluntarily submit to us.</li>
            <li><strong className="text-white">MongoDB Atlas (hosted, U.S. region)</strong> — encrypted database storage.</li>
            <li><strong className="text-white">Government &amp; regulatory bodies</strong> — where required by subpoena, court order, or applicable state-licensing statute (e.g., Maryland State Police Handgun Wear &amp; Carry Permit records; D.C. MPD Special Police Officer commission records).</li>
            <li><strong className="text-white">Our range partners &amp; certified assistant instructors</strong> — only the roster information necessary to run your specific class.</li>
          </ul>
        </Section>

        <Section n={7} title="Your rights & choices">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Access &amp; correction:</strong> email <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> to review or correct the personal data we hold on you.</li>
            <li><strong className="text-white">Deletion:</strong> you may request deletion at any time; we will honor it unless a Maryland or federal statute requires us to retain the record (e.g., firearms-training rosters, tax records).</li>
            <li><strong className="text-white">Marketing opt-out:</strong> unsubscribe from any email footer, reply STOP to any SMS, or contact us in writing.</li>
            <li><strong className="text-white">Do-Not-Track:</strong> we honor Global Privacy Control (GPC) signals for opt-out preferences where applicable.</li>
          </ul>
        </Section>

        <Section n={8} title="Data security">
          <ul className="list-disc pl-6 space-y-1">
            <li>All traffic to seprotection.com is served over TLS 1.2+.</li>
            <li>Passwords are stored using bcrypt with per-user salting.</li>
            <li>Payment data is tokenized by our payment processor; SEP servers never receive card numbers.</li>
            <li>Access to production databases is restricted to authorized personnel and logged.</li>
            <li>In the event of a breach that triggers notification obligations under the <em>Maryland Personal Information Protection Act (Md. Code, Com. Law § 14-3501 et seq.)</em>, we will notify affected Maryland residents and the Office of the Maryland Attorney General as required.</li>
          </ul>
        </Section>

        <Section n={9} title="Data retention">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Training records &amp; certificates:</strong> retained for a minimum of 7 years to satisfy Maryland State Police and D.C. MPD recordkeeping obligations.</li>
            <li><strong className="text-white">Payment records:</strong> 7 years (federal tax retention).</li>
            <li><strong className="text-white">Marketing lists:</strong> until you unsubscribe, plus a permanent suppression record.</li>
            <li><strong className="text-white">Web server logs:</strong> maximum 90 days.</li>
          </ul>
        </Section>

        <Section n={10} title="Children's information">
          <p>
            The Services are not directed to children under 13. We do not knowingly collect personal information from
            children under 13. If we discover we have done so, we will delete it promptly. Firearms and security courses
            are further restricted by statute to adults (18+ or 21+ depending on the course).
          </p>
        </Section>

        <Section n={11} title="Governing law & venue">
          <p>
            This Policy is governed by the laws of the United States of America and the State of Maryland, without regard
            to conflict-of-laws principles. Any dispute arising from or relating to this Policy shall be brought
            exclusively in the state or federal courts located in Prince George's County, Maryland, and you consent to
            personal jurisdiction and venue in those courts.
          </p>
        </Section>

        <Section n={12} title="Changes to this Policy">
          <p>
            We may update this Policy from time to time. The "Effective" date at the top of the page will always reflect
            the current version. Material changes will be announced at the top of the page and — for registered account
            holders — by email.
          </p>
        </Section>

        <div className="border-t border-white/10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
            Questions? · <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> · 410-645-0722
          </div>
          <Link to="/refund" className="link-gold font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="privacy-refund-link">
            → Refund Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
